/**
 * Real-time camera compositor: person cutout over a template background.
 */

import { createCanvasCaptureStream } from '@/lib/pitch/canvasCaptureStream';
import { startFrameLoop } from '@/lib/pitch/frameLoop';
import {
  closePitchSelfieSegmenter,
  createPitchSelfieSegmenter,
} from '@/lib/pitch/pitchPersonSegmentation';
import { MASK_EMA_ALPHA } from '@/lib/pitch/pitchMaskStabilizer';
import {
  PITCH_RECORD_HEIGHT,
  PITCH_RECORD_WIDTH,
} from '@/lib/pitch/pitchRecordQuality';

const DEFAULT_WIDTH = PITCH_RECORD_WIDTH;
const DEFAULT_HEIGHT = PITCH_RECORD_HEIGHT;
const MASK_FEATHER_PX = 6;
const MASK_PROCESS_SCALE = 0.5;
const SEGMENTATION_INTERVAL_MS = 80;

function drawCover(ctx, img, cw, ch) {
  const iw = img.naturalWidth || img.videoWidth || img.width;
  const ih = img.naturalHeight || img.videoHeight || img.height;
  if (!iw || !ih) return;
  const ir = iw / ih;
  const cr = cw / ch;
  let dw;
  let dh;
  let dx;
  let dy;
  if (ir > cr) {
    dh = ch;
    dw = ch * ir;
    dx = (cw - dw) / 2;
    dy = 0;
  } else {
    dw = cw;
    dh = cw / ir;
    dx = 0;
    dy = (ch - dh) / 2;
  }
  ctx.drawImage(img, dx, dy, dw, dh);
}

function drawMirrored(ctx, source, w, h) {
  ctx.save();
  ctx.translate(w, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(source, 0, 0, w, h);
  ctx.restore();
}

async function loadImage(url) {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`Failed to load background image (${res.status}): ${url}`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to decode background image: ${url}`));
      img.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function waitForVideoReady(video) {
  if (video.readyState >= 2) return;
  await new Promise((resolve, reject) => {
    const onReady = () => {
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('error', onError);
      resolve();
    };
    const onError = () => {
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('error', onError);
      reject(new Error('Camera video failed to load'));
    };
    video.addEventListener('loadeddata', onReady);
    video.addEventListener('error', onError);
  });
}

/**
 * @returns {Promise<{ stream: MediaStream, stop: () => void, canvas: HTMLCanvasElement }>}
 */
export async function createPitchCameraCompositor({
  videoStream,
  backgroundImageUrl,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
} = {}) {
  if (!videoStream) {
    throw new Error('videoStream is required');
  }
  const bgUrl = String(backgroundImageUrl || '').trim();
  if (!bgUrl) {
    throw new Error('backgroundImageUrl is required');
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) {
    throw new Error('Canvas 2D context unavailable');
  }

  const personCanvas = document.createElement('canvas');
  personCanvas.width = width;
  personCanvas.height = height;
  const personCtx = personCanvas.getContext('2d');

  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = width;
  maskCanvas.height = height;
  const maskCtx = maskCanvas.getContext('2d');

  const maskW = Math.max(160, Math.round(width * MASK_PROCESS_SCALE));
  const maskH = Math.max(90, Math.round(height * MASK_PROCESS_SCALE));

  const rawMaskCanvas = document.createElement('canvas');
  rawMaskCanvas.width = maskW;
  rawMaskCanvas.height = maskH;
  const rawMaskCtx = rawMaskCanvas.getContext('2d');

  const smoothMaskCanvas = document.createElement('canvas');
  smoothMaskCanvas.width = maskW;
  smoothMaskCanvas.height = maskH;
  const smoothMaskCtx = smoothMaskCanvas.getContext('2d');

  const blendMaskCanvas = document.createElement('canvas');
  blendMaskCanvas.width = maskW;
  blendMaskCanvas.height = maskH;
  const blendMaskCtx = blendMaskCanvas.getContext('2d');

  if (!personCtx || !maskCtx || !rawMaskCtx || !smoothMaskCtx || !blendMaskCtx) {
    throw new Error('Canvas 2D context unavailable for person mask');
  }

  const video = document.createElement('video');
  video.srcObject = videoStream;
  video.muted = true;
  video.playsInline = true;
  video.setAttribute('playsinline', 'true');
  await video.play();
  await waitForVideoReady(video);

  const background = await loadImage(bgUrl);
  const segmenter = await createPitchSelfieSegmenter();

  let stopped = false;
  let segmentBusy = false;
  let lastSegmentAt = 0;
  let hasMask = false;
  let maskFrames = 0;

  const drawBackground = () => {
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, width, height);
    drawCover(ctx, background, width, height);
  };

  const updateSmoothedMask = (segmentationMask) => {
    rawMaskCtx.clearRect(0, 0, maskW, maskH);
    drawMirrored(rawMaskCtx, segmentationMask, maskW, maskH);

    blendMaskCtx.clearRect(0, 0, maskW, maskH);
    if (maskFrames > 0) {
      blendMaskCtx.globalAlpha = 1;
      blendMaskCtx.drawImage(smoothMaskCanvas, 0, 0);
      blendMaskCtx.globalAlpha = maskFrames < 3 ? 0.55 : MASK_EMA_ALPHA;
      blendMaskCtx.drawImage(rawMaskCanvas, 0, 0);
      blendMaskCtx.globalAlpha = 1;
    } else {
      blendMaskCtx.drawImage(rawMaskCanvas, 0, 0);
    }

    smoothMaskCtx.clearRect(0, 0, maskW, maskH);
    smoothMaskCtx.filter = 'blur(2px)';
    smoothMaskCtx.drawImage(blendMaskCanvas, 0, 0);
    smoothMaskCtx.filter = 'none';
    maskFrames += 1;
    hasMask = true;
  };

  const renderPersonLayer = (personSource) => {
    maskCtx.clearRect(0, 0, width, height);
    maskCtx.filter = `blur(${MASK_FEATHER_PX}px)`;
    maskCtx.imageSmoothingEnabled = true;
    maskCtx.imageSmoothingQuality = 'high';
    maskCtx.drawImage(smoothMaskCanvas, 0, 0, width, height);
    maskCtx.filter = 'none';

    personCtx.clearRect(0, 0, width, height);
    drawMirrored(personCtx, personSource, width, height);
    personCtx.globalCompositeOperation = 'destination-in';
    personCtx.drawImage(maskCanvas, 0, 0, width, height);
    personCtx.globalCompositeOperation = 'source-over';
  };

  let capture = null;
  let recordCapture = null;

  const pushAllFrames = () => {
    capture?.pushFrame?.();
    recordCapture?.pushFrame?.();
  };

  const paintLiveFrame = () => {
    if (video.readyState < 2) {
      drawBackground();
      return;
    }
    drawBackground();
    if (hasMask) {
      renderPersonLayer(video);
      ctx.drawImage(personCanvas, 0, 0, width, height);
    } else {
      // Keep recording motion while MediaPipe warms up / fails — never a still bg plate.
      drawMirrored(ctx, video, width, height);
    }
  };

  segmenter.onResults((results) => {
    if (stopped || !results?.segmentationMask) return;
    try {
      updateSmoothedMask(results.segmentationMask);
      drawBackground();
      renderPersonLayer(results.image || video);
      ctx.drawImage(personCanvas, 0, 0, width, height);
      pushAllFrames();
    } catch (err) {
      console.warn('Pitch segmentation frame failed:', err);
    }
  });

  paintLiveFrame();

  try {
    capture = createCanvasCaptureStream(canvas);
  } catch (err) {
    closePitchSelfieSegmenter(segmenter);
    video.pause();
    video.srcObject = null;
    throw err;
  }

  const loop = (now) => {
    if (stopped) return;
    if (video.paused) {
      video.play().catch(() => {});
    }
    paintLiveFrame();
    pushAllFrames();

    if (segmentBusy || video.readyState < 2) return;
    if (now - lastSegmentAt < SEGMENTATION_INTERVAL_MS) return;
    lastSegmentAt = now;
    segmentBusy = true;
    segmenter.send({ image: video }).catch((err) => {
      console.warn('Pitch segmentation send failed:', err);
    }).finally(() => {
      segmentBusy = false;
    });
  };
  const stopLoop = startFrameLoop(loop, { fps: 30 });

  // Kick off first segmentation immediately.
  segmentBusy = true;
  segmenter.send({ image: video }).catch((err) => {
    console.warn('Pitch segmentation bootstrap failed:', err);
  }).finally(() => {
    segmentBusy = false;
  });

  const stop = () => {
    stopped = true;
    stopLoop();
    try {
      recordCapture?.stop?.();
    } catch {
      /* ignore */
    }
    recordCapture = null;
    capture?.stop?.();
    capture = null;
    closePitchSelfieSegmenter(segmenter);
    video.pause();
    video.srcObject = null;
  };

  /**
   * Fresh MediaStream for each MediaRecorder session. Reusing one canvas track
   * across start/stop cycles often yields black video after the first take.
   * Preview keeps using the original stream.
   */
  const openRecordStream = () => {
    if (stopped) throw new Error('Camera compositor already stopped');
    try {
      recordCapture?.stop?.();
    } catch {
      /* ignore */
    }
    recordCapture = createCanvasCaptureStream(canvas);
    paintLiveFrame();
    pushAllFrames();
    return recordCapture.stream;
  };

  const releaseRecordStream = () => {
    try {
      recordCapture?.stop?.();
    } catch {
      /* ignore */
    }
    recordCapture = null;
  };

  return {
    stream: capture.stream,
    openRecordStream,
    releaseRecordStream,
    stop,
    canvas,
  };
}

export function pickVideoRecorderMimeType() {
  const candidates = [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ];
  return candidates.find((type) => {
    try {
      return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type);
    } catch {
      return false;
    }
  }) || 'video/webm';
}

export function pickAudioRecorderMimeType() {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];
  return candidates.find((type) => {
    try {
      return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type);
    } catch {
      return false;
    }
  }) || '';
}

export function audioBlobTypeFromMime(mime) {
  const m = String(mime || '').trim();
  if (m) return m.split(';')[0];
  return 'audio/webm';
}

export function audioFileExtensionFromMime(mime) {
  const base = audioBlobTypeFromMime(mime);
  if (base.includes('mp4')) return 'm4a';
  if (base.includes('ogg')) return 'ogg';
  return 'webm';
}
